import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getMe, acceptPrivacy as acceptPrivacyApi, exchangeHandoff, refreshSession } from './api';
import { takeHandoffCode } from './handoff';
import { wisSessieGeheugen } from '../sessieOpslag';
import { installeerSessieFetch, maakVertaler, SESSIE_CODES } from './sessieFetch';

const AuthContext = createContext(null);

// Schuivende sessie. Een token leeft 24 u (backend TOKEN_TTL); zolang het portaal
// zichtbaar in gebruik is, vragen we om de VERLENG_NA_MS een vers token en sturen
// dat via de fetch-wrapper mee (het React-`token` verandert daarbij niet, zie
// sessieFetch.js). Wie het portaal langer dan 24 u links laat liggen, wordt bij
// terugkomst netjes afgemeld met uitleg in plaats van "Ongeldig token" per actie.
const VERLENG_NA_MS = 15 * 60 * 1000;
const CONTROLE_ELKE_MS = 60 * 1000;

export const MELDING_VERLOPEN = 'Je sessie is verlopen. Meld je opnieuw aan om verder te gaan.';
const MELDING_ONGELDIG = 'Je bent afgemeld omdat je sessie niet meer geldig was. Meld je opnieuw aan.';

function meldingVoor(code) {
  return code === 'session_expired' ? MELDING_VERLOPEN : MELDING_ONGELDIG;
}

// Uitgiftetijd (ms) uit een JWT, zonder handtekening te controleren — enkel om
// te weten of het tijd is voor een verlenging. Onleesbaar → "oud".
export function tokenUitgegevenOp(token) {
  try {
    const deel = String(token).split('.')[1];
    const json = atob(deel.replace(/-/g, '+').replace(/_/g, '/'));
    const iat = JSON.parse(json).iat;
    return Number.isFinite(iat) ? iat * 1000 : 0;
  } catch { return 0; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState(null);
  // Waarom iemand op het loginscherm staat (sessie verlopen …); leeg na aanmelden.
  const [sessieMelding, setSessieMelding] = useState('');
  const vertaler = useRef(maakVertaler());

  function afmelden(melding) {
    wisSessieGeheugen();
    localStorage.removeItem('auth_token');
    vertaler.current.wis();
    setToken(null);
    setUser(null);
    setImpersonatedBy(null);
    setSessieMelding(melding || '');
  }

  // Fetch-wrapper: verlengd token meesturen + verlopen sessie herkennen. Staat
  // vóór de token-bootstrap, zodat ook de eerste getMe eronder valt.
  useEffect(() => {
    return installeerSessieFetch({
      vertaler: vertaler.current,
      opVerlopen: (code) => afmelden(meldingVoor(code)),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Binnenkomende sessie-overdracht van een ander merkdomein (?handoff=<code>).
  // De code wordt bij het lezen meteen uit de URL gehaald. Zolang de uitwisseling
  // loopt, slaan we de gewone token-bootstrap over: een eventueel oud token op
  // deze origin moet niet winnen van de sessie die net wordt overgedragen.
  const [handoffCode] = useState(() => takeHandoffCode());
  const [handoffBusy, setHandoffBusy] = useState(() => !!handoffCode);

  useEffect(() => {
    if (!handoffCode) return;
    let alive = true;
    exchangeHandoff(handoffCode)
      .then((d) => {
        if (!alive || !d.token) return;
        localStorage.setItem('auth_token', d.token);
        setToken(d.token);
      })
      .catch(() => { /* verlopen of al gebruikt → gewoon het loginscherm */ })
      .finally(() => { if (alive) setHandoffBusy(false); });
    return () => { alive = false; };
  }, [handoffCode]);

  useEffect(() => {
    if (handoffBusy) return; // wacht tot de overdracht klaar is
    if (!token) {
      setLoading(false);
      return;
    }
    getMe(token)
      .then((data) => {
        setUser(data.user);
        setImpersonatedBy(data.impersonatedBy || null);
      })
      .catch((err) => {
        // Een verlopen/ongeldig token → loginscherm mét reden. Andere fouten
        // (netwerk) blijven zoals vroeger: token weg, loginscherm zonder melding.
        afmelden(err && SESSIE_CODES.has(err.code) ? meldingVoor(err.code) : '');
      })
      .finally(() => setLoading(false));
  }, [token, handoffBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Schuivende sessie: elke minuut kijken, verlengen zodra het huidige token
  // ouder is dan VERLENG_NA_MS én het portaal zichtbaar is. Ook meteen wanneer
  // het tabblad weer zichtbaar wordt (PWA-venster dat een dag dicht stond):
  // is het token intussen verlopen, dan meldt de fetch-wrapper de gebruiker af.
  useEffect(() => {
    if (!token || !user) return;
    let bezig = false;
    const verleng = async () => {
      if (bezig) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const huidig = vertaler.current.huidig(token);
      if (Date.now() - tokenUitgegevenOp(huidig) < VERLENG_NA_MS) return;
      bezig = true;
      try {
        const d = await refreshSession(huidig);
        if (d && d.token) {
          localStorage.setItem('auth_token', d.token);
          vertaler.current.noteer(huidig, d.token);
        }
      } catch { /* sessiecodes handelt de fetch-wrapper af; netwerkfouten: volgende keer */ }
      finally { bezig = false; }
    };
    verleng();
    const timer = setInterval(verleng, CONTROLE_ELKE_MS);
    const opZichtbaar = () => { if (document.visibilityState === 'visible') verleng(); };
    document.addEventListener('visibilitychange', opZichtbaar);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', opZichtbaar); };
  }, [token, user]);

  function loginWithToken(newToken, userData, impersonator) {
    // Elke aanmelding begint met een schone lei: pagina, subpagina's en filters
    // van een vorige sessie (of van de gebruiker die we net waren, bij impersonatie)
    // horen niet stilletjes mee te reizen. Anders opent iemand dagen later een
    // pagina die maar een deel van de data toont zonder dat hij nog weet waarom.
    wisSessieGeheugen();
    vertaler.current.wis();
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    setImpersonatedBy(impersonator || null);
    setSessieMelding('');
  }

  function logout() {
    afmelden('');
  }

  async function acceptPrivacy() {
    const data = await acceptPrivacyApi(token);
    setUser((prev) => ({ ...prev, privacyAcceptedAt: data.privacyAcceptedAt }));
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated: !!user, impersonatedBy, sessieMelding, loginWithToken, logout, acceptPrivacy }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
